import { AsYouType, getCountryCallingCode, parseDigits, parsePhoneNumberFromString } from 'libphonenumber-js/max';
import type { CountryCode } from 'libphonenumber-js/max';
import type { PhoneErrorCode } from '@/types/PhoneErrorCode';
import type { PhoneErrorInfo } from '@/types/PhoneErrorInfo';
import type { PhoneNumber } from 'libphonenumber-js/max';
import type { Track } from '@react-input/mask';

/*
 * Punctuation of the user-facing strings below, matching `validation-utils.ts`:
 * - A control label (button, link) never ends with a period, because it names an action
 *   rather than stating something. This covers the resend label and its countdown variants.
 * - A status or validation message of one sentence carries no period either.
 * - A message of two or more sentences is punctuated throughout, final sentence included.
 * Keep new strings on this rule; mixed punctuation across one dialog reads as a defect.
 */

/**
 * Fallback resend cooldown in seconds, used only until `GET /rest/settings/sms` responds.
 * Matches the `sms.resend.cooldown-seconds` default
 * so the countdown is already correct on an unconfigured environment.
 * The backend remains the authority.
 */
export const SMS_RESEND_COOLDOWN_SECONDS = 60;

/**
 * Fallback resend block duration in seconds, used only until `GET /rest/settings/sms` responds.
 * Matches the `sms.resend.block-seconds` default.
 */
export const SMS_RESEND_BLOCK_SECONDS = 30 * 60;

/**
 * Label of the button that advances from the phone-entry step to the code step.
 */
export const SMS_MESSAGE_NEXT_STEP = 'Далее';

/**
 * Label of the button that submits the entered SMS code.
 */
export const SMS_MESSAGE_CONFIRM_CODE = 'Подтвердить';

/**
 * Label of the resend button when a resend is allowed.
 */
export const SMS_MESSAGE_RESEND_CODE = 'Отправить код повторно';

/**
 * Prefix for the resend-block message; the remaining time is appended.
 * The newline breaks the message between its two sentences, so it never wraps mid-sentence.
 * The element rendering it needs `white-space: pre-line` for the break to take effect.
 */
export const SMS_MESSAGE_RESEND_BLOCK_PREFIX = 'Слишком много запросов.\nПовторите попытку через';

/**
 * Message shown when the code was annulled after the maximum failed attempts.
 */
export const SMS_MESSAGE_ATTEMPTS_EXHAUSTED = 'Вы исчерпали попытки ввода. Запросите новый код.';

/**
 * Message shown when there is no active verification code.
 */
export const SMS_MESSAGE_CODE_EXPIRED = 'Срок действия кода истёк. Запросите новый код.';

/**
 * Message shown when the submitted code is empty or incomplete.
 */
export const SMS_MESSAGE_CODE_EMPTY = 'Введите код из SMS';

/**
 * Prefix for the wrong-code message; the attempt count is appended.
 */
export const SMS_MESSAGE_WRONG_CODE_PREFIX = 'Неверный код. Осталось попыток:';

/**
 * Message shown when the number turns out to be confirmed elsewhere at the moment the code is submitted.
 */
export const SMS_MESSAGE_PHONE_ALREADY_BOUND = 'Этот номер уже используется. Вернитесь назад и укажите другой.';

/**
 * Prefix for the resend-countdown message; the M:SS value is appended.
 */
export const SMS_MESSAGE_RESEND_COUNTDOWN_PREFIX = 'Отправить код повторно через';

/**
 * Prefix for the code-sent message; the masked phone is appended.
 */
export const SMS_MESSAGE_CODE_SENT_PREFIX = 'Мы отправили код на номер';

/**
 * Maximum number of national digits after the '+7' country code.
 */
export const RUSSIAN_NATIONAL_DIGITS = 10;

/**
 * Extracts up to 10 national digits from a phone value.
 * Strips the fixed '+7' country prefix, any separators, and a trunk '7'/'8' code
 * so partial and pasted values normalize consistently.
 */
export const toRussianNationalDigits = (value?: string): string => {
  if (!value) {
    return '';
  }
  const withoutPrefix: string = value.startsWith('+7') ? value.slice(2) : value;
  const digits: string = withoutPrefix.replace(/\D/g, '');
  if (digits.length > RUSSIAN_NATIONAL_DIGITS && (digits.startsWith('7') || digits.startsWith('8'))) {
    return digits.slice(1, RUSSIAN_NATIONAL_DIGITS + 1);
  }
  return digits.slice(0, RUSSIAN_NATIONAL_DIGITS);
};

/**
 * The region an entry carrying no country code is read against.
 * A number typed with a `+` is read on its own and reaches whatever country it names.
 */
const ENTRY_DEFAULT_COUNTRY: CountryCode = 'RU';

/**
 * Reduces arbitrary text to the characters a phone number is made of.
 * A `+` counts only in first place; anywhere else it ends the number.
 *
 * @param text The text to reduce.
 */
export const toPhoneEntry = (text?: string): string => {
  if (!text) {
    return '';
  }
  let entry = '';
  for (const character of text) {
    if (character === '+') {
      if (entry) {
        break;
      }
      entry = '+';
      continue;
    }
    entry += parseDigits(character);
  }
  return entry;
};

/**
 * Formats an entry the way the number reads while it is being typed.
 * Unlike `formatRussianPhone` this shows no mask and no country code of its own:
 * the field stays empty until something is entered.
 *
 * @param entry The phone characters entered so far.
 */
export const formatPhoneEntry = (entry?: string): string => new AsYouType(ENTRY_DEFAULT_COUNTRY).input(entry ?? '');

/**
 * Derives the E.164 value a form stores from what has been typed.
 * Empty until there is a number to report — a lone `+` names no country and yields nothing.
 *
 * @param entry The phone characters entered so far.
 */
export const toE164Phone = (entry?: string): string => {
  if (!entry) {
    return '';
  }
  const formatter: AsYouType = new AsYouType(ENTRY_DEFAULT_COUNTRY);
  formatter.input(entry);
  return formatter.getNumberValue() ?? '';
};

/**
 * Reads an E.164 value back into the characters a user would have typed to reach it.
 * A Russian number reads back in national notation, so a number arriving from the profile
 * is shown the way it would be entered here; any other number keeps its international form.
 *
 * The '+7' calling code is shared with Kazakhstan, so the country decides and the calling code alone does not —
 * otherwise a Kazakh number comes back as '8 (701) 234 5678' and reads as a Russian one,
 * which is also how `formatPhoneNumber` in `string-utils` already tells the two apart when it renders a stored number.
 * A value carrying '+7' that belongs to no country at all still reads back nationally:
 * libphonenumber leaves `country` undefined for a number no plan allocates,
 * and showing that in international notation would look like the field rewriting what was entered.
 *
 * @param value The E.164 value held by the form.
 */
export const toPhoneEntryFromE164 = (value?: string): string => {
  if (!value) {
    return '';
  }
  const formatter: AsYouType = new AsYouType(undefined);
  formatter.input(value);
  const phoneNumber: PhoneNumber | undefined = formatter.getNumber();
  if (!phoneNumber) {
    return '';
  }
  const sharesCallingCode: boolean = phoneNumber.countryCallingCode === getCountryCallingCode(ENTRY_DEFAULT_COUNTRY);
  const isNotAnotherCountry: boolean =
    phoneNumber.country === undefined || phoneNumber.country === ENTRY_DEFAULT_COUNTRY;
  if (sharesCallingCode && isNotAnotherCountry) {
    return parseDigits(phoneNumber.formatNational());
  }
  return value;
};

/**
 * Renders national digits as the fixed mask '+7 (XXX) XXX-XX-XX'.
 * Separators appear progressively as digits are entered; the '+7 ' prefix is always shown.
 */
export const formatRussianPhone = (value?: string): string => {
  const digits: string = toRussianNationalDigits(value);
  let result: string = '+7 (' + digits.slice(0, 3);
  if (digits.length > 3) {
    result += ') ' + digits.slice(3, 6);
  }
  if (digits.length > 6) {
    result += '-' + digits.slice(6, 8);
  }
  if (digits.length > 8) {
    result += '-' + digits.slice(8, RUSSIAN_NATIONAL_DIGITS);
  }
  return result;
};

/**
 * Masks a Russian phone number for confirmation displays,
 * keeping the country code, area code, and last two digits visible (e.g. `'+7 (912) •••-••-89'`).
 * Falls back to the full formatted value when fewer than 10 national digits are available,
 * so partial input is never mis-masked.
 */
export const maskRussianPhone = (phone?: string): string => {
  const digits: string = toRussianNationalDigits(phone);
  if (digits.length < RUSSIAN_NATIONAL_DIGITS) {
    return formatRussianPhone(phone);
  }
  const area: string = digits.slice(0, 3);
  const lastTwo: string = digits.slice(8, RUSSIAN_NATIONAL_DIGITS);
  const bullet = '•';
  return `+7 (${area}) ${bullet}${bullet}${bullet}-${bullet}${bullet}-${lastTwo}`;
};

/**
 * Normalizes inserted text to Russian national digits before it is masked,
 * so pasting '+7 912 345-67-89', '8 (912) 345-67-89', or '9123456789' all yield the same result.
 * Non-digits collapse to an empty string, which the mask engine treats as "insert nothing",
 * so letters and punctuation are ignored.
 * Deletions carry no data and pass straight through, leaving the engine to remove
 * whatever the caret covers; normalizing them would re-insert the text just removed.
 */
export const trackRussianPhone: Track = (tracking) =>
  tracking.inputType === 'insert' ? toRussianNationalDigits(tracking.data) : tracking.data;

/**
 * Formats a remaining-seconds value as M:SS.
 * For example, `59` becomes `'0:59'` and `1799` becomes `'29:59'`.
 * @param totalSeconds Seconds left.
 * @param padMinutes   Pads the minutes to two digits, so a countdown that crosses ten minutes keeps
 *                     a constant character count and does not resize the label it sits in.
 *                     Pointless below ten minutes, where the minutes are a single digit throughout.
 */
export const formatMinutesSeconds = (totalSeconds?: number, padMinutes?: boolean): string => {
  const seconds: number = Math.max(0, Math.floor(totalSeconds ?? 0));
  const minutes: number = Math.floor(seconds / 60);
  const remainder: number = seconds % 60;
  const renderedMinutes: string = padMinutes ? minutes.toString().padStart(2, '0') : minutes.toString();
  return `${renderedMinutes}:${remainder.toString().padStart(2, '0')}`;
};

/**
 * Builds the wrong-code message including the remaining attempt count.
 * Two sentences, so it terminates with a period, matching {@link SMS_MESSAGE_ATTEMPTS_EXHAUSTED}
 * and {@link SMS_MESSAGE_CODE_EXPIRED}.
 * @param remainingAttempts Tries left, as reported by the backend.
 */
export const formatWrongCodeMessage = (remainingAttempts?: number): string =>
  `${SMS_MESSAGE_WRONG_CODE_PREFIX}\u00A0${remainingAttempts ?? 0}.`;

/**
 * Builds the resend-countdown message including the M:SS countdown.
 * @param seconds Seconds left until the next resend is allowed.
 */
export const formatResendCountdownMessage = (seconds?: number): string =>
  `${SMS_MESSAGE_RESEND_COUNTDOWN_PREFIX}\u00A0${formatMinutesSeconds(seconds)}`;

/**
 * Builds the resend-block message including the M:SS countdown.
 * The block reuses the same countdown treatment as the cooldown, so the remaining time stays live
 * instead of restating the configured duration for the whole block.
 * At the default 1800 seconds it starts as
 * «Слишком много запросов. Повторите попытку через 30:00».
 * @param seconds Seconds left until the block lifts.
 */
export const formatResendBlockMessage = (seconds?: number): string =>
  `${SMS_MESSAGE_RESEND_BLOCK_PREFIX}\u00A0${formatMinutesSeconds(seconds, true)}`;

/**
 * Display category derived from a backend {@link PhoneErrorCode}.
 */
export type PhoneErrorKind =
  | 'wrong'
  | 'exhausted'
  | 'expired'
  | 'empty'
  | 'cooldown'
  | 'block'
  | 'alreadyBound'
  | 'unknown';

/**
 * Maps a raw backend error-code string to a stable display category.
 * Unknown or missing codes resolve to `'unknown'`.
 */
export const resolvePhoneErrorKind = (code?: string): PhoneErrorKind => {
  switch (code as PhoneErrorCode) {
    case 'CODE_WRONG':
      return 'wrong';
    case 'CODE_ATTEMPTS_EXHAUSTED':
      return 'exhausted';
    case 'CODE_EXPIRED':
      return 'expired';
    case 'CODE_EMPTY':
      return 'empty';
    case 'RESEND_COOLDOWN_ACTIVE':
      return 'cooldown';
    case 'RESEND_BLOCK_ACTIVE':
      return 'block';
    case 'PHONE_ALREADY_BOUND':
      return 'alreadyBound';
    default:
      return 'unknown';
  }
};

/**
 * Safely extracts a {@link PhoneErrorInfo} from an RTK Query rejection,
 * tolerating both the new {@link PhoneErrorResponse} body and legacy error bodies.
 */
export const extractPhoneError = (error: unknown): PhoneErrorInfo | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const rejection = error as { status?: number; data?: Record<string, unknown> };
  const data: Record<string, unknown> | undefined = rejection?.data;
  if (!data) {
    return undefined;
  }
  const code = typeof data.code === 'string' ? (data.code as PhoneErrorCode) : undefined;
  const remainingAttempts = typeof data.remainingAttempts === 'number' ? data.remainingAttempts : undefined;
  const secondsRemaining = typeof data.secondsRemaining === 'number' ? data.secondsRemaining : undefined;
  const message = typeof data.message === 'string' ? data.message : undefined;
  return { status: rejection.status, code, remainingAttempts, secondsRemaining, message };
};

/**
 * Parses a login identifier as a Russian phone, or returns `undefined` when it is not one.
 */
const parseRussianLoginPhone = (value?: string): PhoneNumber | undefined => {
  if (!value || value.includes('@')) {
    return undefined;
  }
  const parsed: PhoneNumber | undefined = parsePhoneNumberFromString(value, 'RU');
  return parsed?.country === 'RU' && parsed.isValid() ? parsed : undefined;
};

/**
 * Whether the value carries a complete Russian phone number.
 * Used by the unified login field to tell a phone from an email.
 */
export const isLoginIdentifierPhone = (value?: string): boolean => parseRussianLoginPhone(value) !== undefined;

/**
 * Normalizes a Russian phone to the '+7XXXXXXXXXX' canonical form used by the backend login lookup.
 * Returns the value unchanged when it is not a Russian phone,
 * so an email — or anything else the login field accepted — passes through untouched.
 */
export const toCanonicalLoginPhone = (value?: string): string => parseRussianLoginPhone(value)?.number ?? value ?? '';
